/**
 * AtmosphereConfig — physical parameters for Hillaire 2020 sky/atmosphere.
 *
 * All distances are in **kilometres** and all scattering / absorption
 * coefficients are in **km⁻¹**. Using km throughout avoids floating-point
 * precision loss at planet-scale radii (6360 km for Earth).
 *
 * Defaults are Earth-at-sea-level tuned by the Hillaire reference
 * (UE SkyAtmosphere and github.com/JolifantoBambla/webgpu-sky-atmosphere).
 * Modifying any of these requires re-baking the transmittance and
 * multi-scattering LUTs. The SkyModule emits `sky:config-changed` when it
 * detects a change.
 *
 * Reference:
 *   Sébastien Hillaire, "A Scalable and Production Ready Sky and Atmosphere
 *   Rendering Technique", Eurographics Symposium on Rendering 2020.
 *   https://sebh.github.io/publications/egsr2020.pdf
 */

export interface Vec3 { x: number; y: number; z: number }

export interface AtmosphereConfig {
  /** Planet surface radius (km). */
  bottomRadius: number;
  /** Atmosphere top radius (km). */
  topRadius: number;

  /** Rayleigh scattering per RGB (km⁻¹). */
  rayleighScattering: Vec3;
  /** Rayleigh exponential-density scale height (km). */
  rayleighScaleHeight: number;

  /** Mie scattering per RGB (km⁻¹); usually all equal (grey aerosol). */
  mieScattering: Vec3;
  /** Mie extinction (scattering + absorption) per RGB (km⁻¹). */
  mieExtinction: Vec3;
  /** Mie exponential-density scale height (km). */
  mieScaleHeight: number;
  /**
   * THE BACKGROUND AEROSOL (2026-10-06, the sunset-realism work): the share of the sea-level Mie density carried by a
   * second, thin layer with its own scale height — the free troposphere's and the stratosphere's aerosol, which the
   * 1.2 km boundary layer leaves out (it holds e^(−20/1.2) ≈ 6·10⁻⁸ of its density at 20 km, where the measured
   * background is ~2·10⁻⁴/km). That aerosol is what still sees the sun after sunset: its forward-scattered light is the
   * bright orange twilight arch over the set sun. density = (1 − f)·e^(−h/H) + f·e^(−h/H_bg); the sea-level density (the
   * visibility) is unchanged. Absent or 0 = the single boundary layer, bit for bit.
   */
  mieBackgroundFraction?: number;
  /** The background layer's scale height (km); default 8. */
  mieBackgroundScaleHeight?: number;
  /**
   * Mie phase-function asymmetry. Positive = forward-scatter (sun corona).
   * Cornette-Shanks is the reference phase function used at evaluation.
   */
  miePhaseG: number;

  /**
   * Ozone extinction per RGB (km⁻¹). Ozone has a "tent" density profile
   * (triangular peak around 25 km altitude) — encoded via two linear layers.
   */
  ozoneExtinction: Vec3;
  /** Altitude (km) where ozone peaks. */
  ozoneCenterAltitude: number;
  /** Half-width (km) of the ozone tent (linear rise then fall). */
  ozoneHalfWidth: number;

  /** Ground diffuse albedo per RGB; influences bounced light. */
  groundAlbedo: Vec3;
}

/** Earth-at-sea-level defaults (Hillaire 2020 table 1 + UE reference). */
export const EARTH_ATMOSPHERE: AtmosphereConfig = Object.freeze({
  bottomRadius: 6360.0,
  topRadius:    6420.0,

  rayleighScattering: { x: 0.005802, y: 0.013558, z: 0.033100 },
  rayleighScaleHeight: 8.0,

  mieScattering: { x: 0.003996, y: 0.003996, z: 0.003996 },
  mieExtinction: { x: 0.004440, y: 0.004440, z: 0.004440 },
  mieScaleHeight: 1.2,
  miePhaseG: 0.8,

  ozoneExtinction: { x: 0.000650, y: 0.001881, z: 0.000085 },
  ozoneCenterAltitude: 25.0,
  ozoneHalfWidth: 15.0,

  groundAlbedo: { x: 0.4, y: 0.4, z: 0.4 },
});

/** Clone the default for user mutation without touching the frozen base. */
export function createAtmosphereConfig(
  overrides: Partial<AtmosphereConfig> = {},
): AtmosphereConfig {
  const merged: AtmosphereConfig = {
    bottomRadius: overrides.bottomRadius ?? EARTH_ATMOSPHERE.bottomRadius,
    topRadius: overrides.topRadius ?? EARTH_ATMOSPHERE.topRadius,
    rayleighScattering: cloneVec3(overrides.rayleighScattering ?? EARTH_ATMOSPHERE.rayleighScattering),
    rayleighScaleHeight: overrides.rayleighScaleHeight ?? EARTH_ATMOSPHERE.rayleighScaleHeight,
    mieScattering: cloneVec3(overrides.mieScattering ?? EARTH_ATMOSPHERE.mieScattering),
    mieExtinction: cloneVec3(overrides.mieExtinction ?? EARTH_ATMOSPHERE.mieExtinction),
    mieScaleHeight: overrides.mieScaleHeight ?? EARTH_ATMOSPHERE.mieScaleHeight,
    mieBackgroundFraction: overrides.mieBackgroundFraction ?? 0,
    mieBackgroundScaleHeight: overrides.mieBackgroundScaleHeight ?? MIE_BACKGROUND_SCALE_HEIGHT,
    miePhaseG: overrides.miePhaseG ?? EARTH_ATMOSPHERE.miePhaseG,
    ozoneExtinction: cloneVec3(overrides.ozoneExtinction ?? EARTH_ATMOSPHERE.ozoneExtinction),
    ozoneCenterAltitude: overrides.ozoneCenterAltitude ?? EARTH_ATMOSPHERE.ozoneCenterAltitude,
    ozoneHalfWidth: overrides.ozoneHalfWidth ?? EARTH_ATMOSPHERE.ozoneHalfWidth,
    groundAlbedo: cloneVec3(overrides.groundAlbedo ?? EARTH_ATMOSPHERE.groundAlbedo),
  };
  return merged;
}

/** Stable string key for change detection (cheaper than deep-equals). */
export function atmosphereConfigKey(cfg: AtmosphereConfig): string {
  return [
    cfg.bottomRadius, cfg.topRadius,
    cfg.rayleighScattering.x, cfg.rayleighScattering.y, cfg.rayleighScattering.z,
    cfg.rayleighScaleHeight,
    cfg.mieScattering.x, cfg.mieScattering.y, cfg.mieScattering.z,
    cfg.mieExtinction.x, cfg.mieExtinction.y, cfg.mieExtinction.z,
    cfg.mieScaleHeight, cfg.miePhaseG,
    cfg.ozoneExtinction.x, cfg.ozoneExtinction.y, cfg.ozoneExtinction.z,
    cfg.ozoneCenterAltitude, cfg.ozoneHalfWidth,
    cfg.groundAlbedo.x, cfg.groundAlbedo.y, cfg.groundAlbedo.z,
    cfg.mieBackgroundFraction ?? 0, cfg.mieBackgroundScaleHeight ?? MIE_BACKGROUND_SCALE_HEIGHT,
  ].map((n) => n.toPrecision(7)).join('|');
}

function cloneVec3(v: Vec3): Vec3 { return { x: v.x, y: v.y, z: v.z }; }

/** The background aerosol layer's default scale height, km (THE BACKGROUND AEROSOL). */
export const MIE_BACKGROUND_SCALE_HEIGHT = 8.0;

// ─── CPU reference helpers (exposed for tests / IBL SH projection) ──────────

/**
 * Rayleigh phase function. `cosTheta = dot(V, L)` (scatter angle).
 *
 *   p_R(µ) = (3 / 16π) * (1 + µ²)
 */
export function rayleighPhase(cosTheta: number): number {
  return (3.0 / (16.0 * Math.PI)) * (1.0 + cosTheta * cosTheta);
}

/**
 * Cornette-Shanks phase function — Hillaire's recommended Mie alternative
 * to Henyey-Greenstein. Smoother forward lobe; physically plausible.
 *
 *   k = (3/(8π)) * (1 - g²) / (2 + g²)
 *   p_M(µ, g) = k * (1 + µ²) / (1 + g² - 2g·µ)^1.5
 *
 * NOTE: some references feed `-cosTheta` to this function when the view
 * and light directions are stored with opposite conventions. Always
 * pass the scattering angle's cosine as-is (V · L).
 */
export function cornetteShanksPhase(cosTheta: number, g: number): number {
  const g2 = g * g;
  const k = (3.0 / (8.0 * Math.PI)) * (1.0 - g2) / (2.0 + g2);
  const denom = Math.pow(1.0 + g2 - 2.0 * g * cosTheta, 1.5);
  return k * (1.0 + cosTheta * cosTheta) / denom;
}

/**
 * Total atmospheric density at altitude h (km above bottomRadius) decomposed
 * as an array [rayleigh, mie, ozone] with each scalar in [0,1].
 *
 * Rayleigh + Mie are exponential with their respective scale heights.
 * Ozone is a tent: rises linearly from `center - halfWidth` to `center`
 * then falls linearly to `center + halfWidth`, clamped to 0 outside.
 */
export function computeDensities(
  cfg: AtmosphereConfig, altitudeKm: number,
): { rayleigh: number; mie: number; ozone: number } {
  const rayleigh = Math.exp(-altitudeKm / cfg.rayleighScaleHeight);
  // THE BACKGROUND AEROSOL: a second, thin layer — evaluated only when the config carries one (absent = bit for bit).
  const f = cfg.mieBackgroundFraction ?? 0;
  const mie = f > 0
    ? (1 - f) * Math.exp(-altitudeKm / cfg.mieScaleHeight) + f * Math.exp(-altitudeKm / (cfg.mieBackgroundScaleHeight ?? MIE_BACKGROUND_SCALE_HEIGHT))
    : Math.exp(-altitudeKm / cfg.mieScaleHeight);
  const distFromCenter = Math.abs(altitudeKm - cfg.ozoneCenterAltitude);
  const ozone = Math.max(0, 1.0 - distFromCenter / cfg.ozoneHalfWidth);
  return { rayleigh, mie, ozone };
}
